// First Created - 26 May 2026
// Author - Atul Pandey
// Purpose - To delete / deactivate a reusable Session QNA question set for Trinity Sessions

import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import Modal from "@/components/common/modal/Modal"

import {
  useGetSessionQNAsQuery,
  useDeleteSessionQNAMutation,
} from "./sessionQNAsApiSlice"

const EtDeleteSessionQNA = ({ id, setActiveAdminView }) => {
  // First Created - 26 May 2026
  // Author - Atul Pandey
  // Purpose - To delete / deactivate a reusable Session QNA question set for Trinity Sessions

  useTitle("Delete Session QNA")

  const { sessionQNA } = useGetSessionQNAsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      sessionQNA: data?.entities[id],
    }),
  })

  if (!sessionQNA) {
    return <PulseLoader color={"#ea580c"} />
  }

  return (
    <EtDeleteSessionQNAForm
      sessionQNA={sessionQNA}
      id={id}
      setActiveAdminView={setActiveAdminView}
    />
  )
}

const EtDeleteSessionQNAForm = ({ sessionQNA, id, setActiveAdminView }) => {
  const navigate = useNavigate()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteMode, setDeleteMode] = useState("soft")

  const [
    deleteSessionQNA,
    {
      isLoading,
      isSuccess,
      isError,
      error,
    },
  ] = useDeleteSessionQNAMutation()

  const [formData, setFormData] = useState({
    sessionQNAName: sessionQNA.sessionQNAName || "",
    sessionQNADesc: sessionQNA.sessionQNADesc || "",
    questionCount: sessionQNA.sessionQNAQuestions?.length || 0,
    activeQuestionCount:
      sessionQNA.sessionQNAQuestions?.filter(question => question?.active !== false)
        ?.length || 0,
    active: sessionQNA.active ?? true,
  })

  useEffect(() => {
    if (isSuccess) {
      setFormData({
        sessionQNAName: "",
        sessionQNADesc: "",
        questionCount: 0,
        activeQuestionCount: 0,
        active: true,
      })

      if (setActiveAdminView) {
        setActiveAdminView("sessionQNAs")
      } else {
        navigate("/sessionQNAs")
      }
    }
  }, [isSuccess])

  const handleOpenModal = (mode = "soft") => {
    setDeleteMode(mode)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => setIsModalOpen(false)

  const handleConfirm = async e => {
    e.preventDefault()

    try {
      await deleteSessionQNA({ id, deleteMode }).unwrap()

      if (setActiveAdminView) {
        setActiveAdminView("sessionQNAs")
      } else {
        navigate("/sessionQNAs")
      }
    } catch (error) {
      console.error(error)
    } finally {
      handleCloseModal()
    }
  }

  const handleCancel = () => {
    if (setActiveAdminView) {
      setActiveAdminView("sessionQNAs")
    } else {
      navigate("/sessionQNAs")
    }
  }

  return (
    <div className="bg-gray-200 p-1 w-3/4 mx-auto mt-5 rounded-lg shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}

      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <div className="bg-white p-1 m-1 rounded">
        <p className="p-1 m-1 font-bold font-poppins text-xl">
          Delete Session QNA
        </p>

        <div className="bg-gray-200 rounded m-1 p-1">
          <form
            onSubmit={e => {
              e.preventDefault()
              handleOpenModal("soft")
            }}
            className="m-1 p-1 text-xs font-poppins"
          >
            <input
              type="text"
              name="sessionQNAName"
              placeholder="Session QNA name"
              value={formData.sessionQNAName}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            <input
              type="text"
              name="sessionQNADesc"
              placeholder="Session QNA description"
              value={formData.sessionQNADesc}
              disabled
              className="w-full rounded p-2 m-1 bg-gray-100"
            />

            <div className="grid grid-cols-3 gap-1">
              <input
                type="text"
                name="questionCount"
                placeholder="Total questions"
                value={`Total Questions: ${formData.questionCount}`}
                disabled
                className="w-full rounded p-2 m-1 bg-gray-100"
              />

              <input
                type="text"
                name="activeQuestionCount"
                placeholder="Active questions"
                value={`Active Questions: ${formData.activeQuestionCount}`}
                disabled
                className="w-full rounded p-2 m-1 bg-gray-100"
              />

              <input
                type="text"
                name="active"
                placeholder="Active status"
                value={formData.active ? "Active" : "Inactive"}
                disabled
                className="w-full rounded p-2 m-1 bg-gray-100"
              />
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded m-1 p-2">
              <p className="text-xs text-orange-700">
                You can either deactivate this Session QNA or permanently delete it.
                Deactivation keeps the QNA set in history. Hard delete removes it
                permanently.
              </p>
            </div>

            <div id="sessionQNAActions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Make Inactive"}
                type={"clear"}
                handleClick={() => handleOpenModal("soft")}
              />

              <EtBtnFormBasic
                name={"Hard Delete"}
                type={"create"}
                handleClick={() => handleOpenModal("hard")}
              />

              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>
          </form>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
      />
    </div>
  )
}

export default EtDeleteSessionQNA